import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError, logInfo } from '../utils/logger';
import { getAuth } from 'firebase/auth';

const SurveyContext = createContext(null);

export const SurveyProvider = ({ children }) => {
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  // Load active survey from localStorage on mount
  useEffect(() => {
    const loadActiveSurvey = async () => {
      try {
        const activeSurveyId = localStorage.getItem('activeSurveyId');
        
        if (!auth.currentUser) {
          localStorage.removeItem('activeSurveyId'); // Clear invalid survey ID if not authenticated
          setLoading(false);
          return;
        }

        if (activeSurveyId) {
          const surveyDoc = await getDoc(doc(db, 'surveys', activeSurveyId));
          if (surveyDoc.exists()) {
            // If the survey exists, set it as active - permissions were already checked when listing
            const surveyData = surveyDoc.data();
            setActiveSurvey({ id: surveyDoc.id, ...surveyData });
            logInfo('Active survey loaded');
          } else {
            logError('Survey not found');
            localStorage.removeItem('activeSurveyId');
            setActiveSurvey(null);
          }
        }
      } catch (error) {
        logError('Failed to load active survey:', error);
        localStorage.removeItem('activeSurveyId');
        setActiveSurvey(null);
      } finally {
        setLoading(false);
      }
    };

    loadActiveSurvey();
  }, [auth.currentUser]);

  const startSurvey = useCallback((survey) => {
    if (!auth.currentUser) {
      logError('Cannot start survey: User not authenticated');
      return;
    }
    setActiveSurvey(survey);
    localStorage.setItem('activeSurveyId', survey.id);
    logInfo('Survey activated:', survey.id);
  }, [auth.currentUser]);

  const updateSurvey = useCallback((data) => {
    if (!auth.currentUser) {
      logError('Cannot update survey: User not authenticated');
      return;
    }
    setActiveSurvey(current => ({
      ...current,
      ...data,
      updatedAt: new Date().toISOString()
    }));
  }, [auth.currentUser]);

  const endSurvey = useCallback(() => {
    setActiveSurvey(null);
    localStorage.removeItem('activeSurveyId');
    logInfo('Survey deactivated');
  }, []);

  return (
    <SurveyContext.Provider value={{ 
      activeSurvey, 
      startSurvey, 
      updateSurvey,
      endSurvey,
      loading
    }}>
      {children}
    </SurveyContext.Provider>
  );
};

export const useSurvey = () => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }
  return context;
};